/**
 * Pure client-side room reducer. The server is the authority: this only folds
 * server events into the last snapshot. It is idempotent under duplicate or
 * re-delivered events (EventSource reconnects replay from Last-Event-ID).
 */
import type { CallOffer, CallLock, RoomEvent, RoomSnapshot } from '@clutch/core';

const STATUS_RANK: Record<CallOffer['status'], number> = { open: 0, closed: 1, resolved: 2, void: 2 };

function upsertLock(locks: CallLock[], lock: CallLock): CallLock[] {
  const i = locks.findIndex((l) => l.callId === lock.callId && l.fanId === lock.fanId);
  if (i === -1) return [...locks, lock];
  const next = locks.slice();
  next[i] = lock;
  return next;
}

export function applyRoomEvent(snapshot: RoomSnapshot | undefined, ev: RoomEvent): RoomSnapshot | undefined {
  if (ev.kind === 'snapshot') return ev.data;
  if (!snapshot) return snapshot; // nothing to fold into until the first snapshot arrives
  if (ev.roomId && ev.roomId !== snapshot.roomId) return snapshot;

  switch (ev.kind) {
    case 'game_event': {
      const { state } = ev.data;
      if (state.asOfSeq <= snapshot.state.asOfSeq) return snapshot;
      return {
        ...snapshot,
        state,
        replay: { ...snapshot.replay, cursorSeq: Math.max(snapshot.replay.cursorSeq, state.asOfSeq) },
      };
    }
    case 'probability': {
      const p = ev.data;
      const last = snapshot.probability[snapshot.probability.length - 1];
      if (last && (p.seq < last.seq || (p.seq === last.seq && p.eventId === last.eventId))) return snapshot;
      return { ...snapshot, probability: [...snapshot.probability, p] };
    }
    case 'moment': {
      const m = ev.data;
      if (!m.isMoment || snapshot.moments.some((x) => x.momentId === m.momentId)) return snapshot;
      return { ...snapshot, moments: [...snapshot.moments, m] };
    }
    case 'call_offer': {
      const cur = snapshot.call;
      if (cur && cur.callId === ev.data.callId && STATUS_RANK[cur.status] > STATUS_RANK[ev.data.status]) {
        return { ...snapshot, call: { ...ev.data, status: cur.status } };
      }
      return { ...snapshot, call: ev.data };
    }
    case 'call_lock': {
      if (ev.data.status !== 'confirmed') return snapshot; // rejections are personal, never shown as locks
      return { ...snapshot, locks: upsertLock(snapshot.locks, ev.data) };
    }
    case 'call_closed': {
      const cur = snapshot.call;
      if (!cur || cur.callId !== ev.data.callId || cur.status !== 'open') return snapshot;
      return { ...snapshot, call: { ...cur, status: 'closed' } };
    }
    case 'presence':
      return { ...snapshot, presence: ev.data };
    case 'reaction':
      return snapshot; // transient; rendered by the room hook, not stored
    case 'resolution': {
      const cur = snapshot.call;
      const call =
        cur && cur.callId === ev.data.callId
          ? { ...cur, status: ev.data.outcome === 'void' ? ('void' as const) : ('resolved' as const) }
          : cur;
      return { ...snapshot, resolution: ev.data, call };
    }
    case 'replay':
      return { ...snapshot, replay: ev.data };
    case 'feed':
      return { ...snapshot, feed: ev.data };
    case 'take':
    case 'take_removed':
      return snapshot; // rendered by the room hook (reaction studio), not stored
    default: {
      const _exhaustive: never = ev;
      return _exhaustive;
    }
  }
}
